/**
 * ChatClient - Lightweight text-only chat client
 *
 * Provides WebSocket-based text messaging without audio dependencies.
 * Designed for chatbot interactions via the Voice Mate chat backend.
 *
 * Features:
 * - WebSocket connection management
 * - Message streaming with chunk accumulation
 * - Event-driven architecture
 * - Session lifecycle management
 * - Error handling and recovery
 *
 * Usage:
 *   const client = new ChatClient({
 *     serverUrl: 'https://your-server.com',
 *     botNumber: '8065250225',
 *     userNumber: 'web_user_123',
 *     language: 'en'
 *   });
 *
 *   client.on('connected', () => console.log('Connected!'));
 *   client.on('message', (text) => console.log('Bot:', text));
 *
 *   await client.connect();
 *   client.sendMessage('Hello!');
 */

import { EventEmitter } from '../core/EventEmitter.js';
import { ConnectionManager } from '../core/ConnectionManager.js';
import { SessionManager } from '../core/SessionManager.js';
import { Logger } from '../utils/Logger.js';
import { Validator } from '../utils/Validator.js';
import { ConnectionError, SessionError, ValidationError } from '../errors/VoiceMateErrors.js';

export class ChatClient extends EventEmitter {
  constructor(config) {
    super();

    // Validate configuration
    Validator.validateConfig(config);

    // Store configuration with defaults
    this.config = {
      serverUrl: config.serverUrl,
      botNumber: config.botNumber,
      userNumber: config.userNumber || 'web_user',
      language: config.language || 'en',
      transport: 'chat',  // Always chat transport
      logLevel: config.logLevel || 'info',
      customHeaders: config.customHeaders || {},
      autoReconnect: config.autoReconnect !== false,
      maxReconnectAttempts: config.maxReconnectAttempts || 5,
      reconnectDelay: config.reconnectDelay || 3000
    };

    // Initialize logger
    this.logger = new Logger(this.config.logLevel);

    // Initialize managers
    this.connectionManager = new ConnectionManager(this.config, this.logger);
    this.sessionManager = new SessionManager(this.config, this.logger);

    // Initialize state
    this.state = {
      connection: 'disconnected',
      messageBuffer: ''
    };

    // Message history
    this.messageHistory = [];

    this.logger.info('ChatClient initialized');
  }

  /**
   * Connect to chat server
   * @returns {Promise<void>}
   */
  async connect() {
    if (this.state.connection !== 'disconnected') {
      this.logger.warn('Already connected or connecting');
      return;
    }

    try {
      this.logger.info('Connecting to chat server...');
      this.state.connection = 'connecting';
      this.emit('connecting');

      // Create session with chat transport
      const sessionData = await this.sessionManager.createSession();
      this.logger.info('Chat session created:', sessionData.session_id);

      // Setup message handlers BEFORE connecting
      this.connectionManager.on('message', (msg) => this.handleMessage(msg));
      this.connectionManager.on('audio:data', (data) => {
        // Ignore audio data in chat client
        this.logger.debug('Ignoring audio data in chat client');
      });

      // Setup connection event handlers BEFORE connecting
      this.connectionManager.on('connected', () => {
        this.state.connection = 'connected';
        this.logger.info('Chat client connected');
        this.emit('connected', sessionData.session_id);
      });

      this.connectionManager.on('disconnected', () => {
        this.state.connection = 'disconnected';
        this.logger.info('Chat client disconnected');
        this.emit('disconnected');
      });

      this.connectionManager.on('reconnecting', (attempt) => {
        this.logger.info(`Reconnecting... (attempt ${attempt})`);
        this.emit('reconnecting', attempt);
      });

      this.connectionManager.on('reconnected', () => {
        this.logger.info('Chat client reconnected');
        this.emit('reconnected');
      });

      this.connectionManager.on('error', (error) => {
        this.logger.error('Connection error:', error);
        this.emit('error', error);
      });

      // Connect WebSocket (now event listeners are ready)
      await this.connectionManager.connect(sessionData.websocket_url);

    } catch (error) {
      this.state.connection = 'disconnected';
      this.logger.error('Failed to connect:', error);
      throw new ConnectionError('Failed to connect to chat server', error);
    }
  }

  /**
   * Disconnect from chat server
   * @returns {Promise<void>}
   */
  async disconnect() {
    this.logger.info('Disconnecting chat client...');

    try {
      // Close WebSocket connection
      this.connectionManager.disconnect();

      // Terminate session
      await this.sessionManager.terminateSession();

      // Reset state
      this.state.connection = 'disconnected';
      this.state.messageBuffer = '';
      this.messageHistory = [];

      this.logger.info('Chat client disconnected successfully');

    } catch (error) {
      this.logger.error('Error during disconnect:', error);
      // Continue cleanup even if errors occur
    }
  }

  /**
   * Send a text message
   * @param {string} text - Message text to send
   * @returns {boolean} True if message was sent, false otherwise
   */
  sendMessage(text) {
    if (!text || typeof text !== 'string' || !text.trim()) {
      this.logger.warn('Cannot send empty message');
      return false;
    }

    if (this.state.connection !== 'connected') {
      this.logger.warn('Cannot send message: not connected');
      throw new ConnectionError('Not connected to chat server');
    }

    const trimmedText = text.trim();

    try {
      // Send message via WebSocket
      this.connectionManager.send({
        type: 'message',
        text: trimmedText
      });

      // Add to message history
      this.messageHistory.push({
        role: 'user',
        text: trimmedText,
        timestamp: Date.now()
      });

      this.logger.info('Message sent:', trimmedText.substring(0, 50));
      return true;

    } catch (error) {
      this.logger.error('Failed to send message:', error);
      throw new ConnectionError('Failed to send message', error);
    }
  }

  /**
   * Handle incoming WebSocket message
   * @param {Object} message - Parsed message object
   * @private
   */
  handleMessage(message) {
    if (!message || !message.type) {
      this.logger.warn('Received invalid message:', message);
      return;
    }

    this.logger.debug('Received message:', message.type);

    switch (message.type) {
      case 'connected':
        this.logger.info('Server confirmed connection');
        break;

      case 'typing':
        this.logger.debug('Bot is typing...');
        this.emit('typing');
        break;

      case 'chunk':
        // Accumulate streaming text chunks
        if (message.text) {
          this.state.messageBuffer += message.text;
          this.logger.debug('Received chunk:', message.text.substring(0, 20));
          this.emit('chunk', message.text);
        }
        break;

      case 'done':
        // Message streaming complete
        const fullText = this.state.messageBuffer;

        if (fullText) {
          // Add to message history
          this.messageHistory.push({
            role: 'assistant',
            text: fullText,
            timestamp: Date.now()
          });

          this.logger.info('Message complete:', fullText.substring(0, 50));
          this.emit('message', fullText);
        }

        // Check if interrupted
        if (message.interrupted) {
          this.logger.info('Message was interrupted');
          this.emit('interrupted');
        }

        // Reset buffer
        this.state.messageBuffer = '';
        break;

      case 'error':
        const errorMsg = message.error || 'Unknown error';
        this.logger.error('Server error:', errorMsg);
        this.emit('error', new Error(errorMsg));
        break;

      default:
        this.logger.debug('Unhandled message type:', message.type);
    }
  }

  /**
   * Get current state
   * @returns {Object} Current state
   */
  getState() {
    return {
      connection: this.state.connection,
      session: this.sessionManager.getSessionInfo(),
      messageCount: this.messageHistory.length
    };
  }

  /**
   * Get message history
   * @returns {Array} Array of message objects
   */
  getMessageHistory() {
    return [...this.messageHistory];
  }

  /**
   * Clear message history
   */
  clearHistory() {
    this.messageHistory = [];
    this.logger.info('Message history cleared');
  }

  /**
   * Send control message (for advanced use)
   * @param {Object} message - Control message object
   */
  sendControl(message) {
    if (this.state.connection !== 'connected') {
      throw new ConnectionError('Not connected');
    }
    this.connectionManager.send(message);
  }
}
